import React, { useState } from 'react';
import {
  Box,
  Avatar,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Photo as PhotoIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';

interface ImageDisplayProps {
  images: string[];
  maxDisplay?: number;
  size?: number;
  showLocationIcon?: boolean;
  locationImage?: string;
  title?: string;
}

const ImageDisplay: React.FC<ImageDisplayProps> = ({
  images = [],
  maxDisplay = 3,
  size = 32,
  showLocationIcon = false,
  locationImage = '',
  title = 'Фото'
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Діагностичне логування
  console.log(`🖼️ ${title}:`, {
    imagesCount: images.length,
    images: images,
    locationImage: locationImage,
    showLocationIcon: showLocationIcon
  });

  const handleImageClick = (image: string) => {
    setSelectedImage(image);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedImage(null);
  };

  const displayedImages = images.slice(0, maxDisplay);
  const remainingCount = images.length - maxDisplay;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Основні фото */}
        {displayedImages.map((image, index) => (
          <Tooltip key={index} title={`${title} ${index + 1}`}>
            <Avatar
              src={image}
              sx={{ 
                width: size, 
                height: size, 
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
              onClick={() => handleImageClick(image)}
            >
              <PhotoIcon />
            </Avatar>
          </Tooltip>
        ))}

        {/* Показуємо кількість додаткових фото */}
        {remainingCount > 0 && (
          <Tooltip title={`Ще ${remainingCount} фото`}>
            <Chip 
              label={`+${remainingCount}`} 
              size="small" 
              variant="outlined"
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                if (images.length > 0) {
                  handleImageClick(images[0]);
                }
              }}
            />
          </Tooltip>
        )}

        {/* Фото локації */}
        {showLocationIcon && locationImage && (
          <Tooltip title="Фото локації">
            <Avatar
              src={locationImage}
              sx={{ 
                width: size, 
                height: size, 
                border: '2px solid #1976d2',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
              onClick={() => handleImageClick(locationImage)}
            >
              <LocationIcon />
            </Avatar>
          </Tooltip>
        )}

        {/* Показуємо повідомлення якщо немає фото */}
        {images.length === 0 && !locationImage && (
          <Chip 
            label="Немає фото" 
            size="small" 
            variant="outlined" 
            sx={{ color: 'text.disabled' }}
          />
        )}
      </Box>

      {/* Діалог для перегляду фото */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {title}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={selectedImage}
                alt={title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageDisplay;
